import type { Ref } from 'vue';
import type {
  PhotoGalleryDTO,
  VideoGalleryDTO,
} from '~/shared/api/client/galleries/galleries.dto';
import {
  EVENT_MEDIA_MODES,
  type EventMediaMode,
} from '~/shared/lib/events-routes.const';
import {
  OG_SITE_NAME,
  OG_TYPE,
  getAbsoluteUrl,
  getOgImageMeta,
} from '~/shared/lib/seo';
import { useOrigin } from '~/shared/lib/useOrigin';
import OgImage from '~/shared/images/og-image.png';
import { EVENTS_GALLERY_DETAIL_CONFIG } from './gallery-detail.const';

type GalleryBase = PhotoGalleryDTO | VideoGalleryDTO;

function getGalleryCover(item: GalleryBase | null): string | null {
  if (!item) {
    return null;
  }

  const cover = 'cover_photo' in item ? item.cover_photo : item.cover_video;
  return cover?.file_url ?? null;
}

export function useEventGalleryDetailSeo(
  gallery: Ref<GalleryBase | null>,
  mode: EventMediaMode,
) {
  const config = EVENTS_GALLERY_DETAIL_CONFIG[mode];
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const galleryData = gallery.value;
    const url = `${origin}${route.fullPath}`;
    const pageTitle =
      galleryData?.description || galleryData?.name || config.fallbackTitle;
    const pageDescription =
      galleryData?.description || galleryData?.name || pageTitle;
    const image =
      getAbsoluteUrl(origin, getGalleryCover(galleryData)) ||
      getAbsoluteUrl(origin, OgImage);
    const schemaType =
      mode === EVENT_MEDIA_MODES.videos ? 'VideoGallery' : 'ImageGallery';
    const listPath =
      mode === EVENT_MEDIA_MODES.videos ? '/events/videos' : '/events/photos';

    return {
      title: pageTitle,
      meta: [
        { name: 'description', content: pageDescription },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: url },
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin, getGalleryCover(galleryData)),
      ],
      script: galleryData
        ? [
            {
              type: 'application/ld+json',
              key: 'ld-json-gallery',
              innerHTML: {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': schemaType,
                    name: galleryData.name,
                    description: pageDescription,
                    image,
                    datePublished: galleryData.publication_date,
                    dateModified: galleryData.updated_datetime,
                    url,
                    publisher: {
                      '@type': 'Organization',
                      name: OG_SITE_NAME,
                      url: origin,
                    },
                  },
                  {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Главная',
                        item: origin,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Мероприятия',
                        item: `${origin}/events`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: config.breadcrumbLabel,
                        item: `${origin}${listPath}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 4,
                        name: galleryData.name,
                        item: url,
                      },
                    ],
                  },
                ],
              },
            },
          ]
        : [],
    };
  });
}
